import { SearchOutlined } from "@ant-design/icons";
import { Card, Skeleton } from "antd";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useLocation } from "react-router-dom";
import HTTP from "../../services/httpService";
import CustomHeader from "../common/Header";
import CustomTable from "../common/Table";

function GitDashboardTable() {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const { state } = useLocation();
    const { range_year, project_id, user_id, range_month } = state;
    const [totalRecords, setTotalRecords] = useState(0);
    const [tableData, setTableData] = useState([]);
    const [formData, setFormData] = useState({
        staff_id,
        range_month,
        range_year,
        project_id,
        user_id,
        page: 0,
        limit: 10,
    });
    const [columns, setColumns] = useState([
        {
            title: "Project",
            dataIndex: "project_name",
            key: "project_id",
            sorter: () => {},
        },
        {
            title: "Tickets",
            dataIndex: "ticket_id",
            key: "ticket_id",
            sorter: () => {},
        },
        {
            title: "Title",
            dataIndex: "title",
            key: "title",
            sorter: () => {},
        },
        {
            title: "Commit Message",
            dataIndex: "message",
            key: "message",
            sorter: () => {},
        },
        {
            title: "Author",
            dataIndex: "author_name",
            key: "author_email",
            sorter: () => {},
        },
        {
            title: "Branch Name",
            dataIndex: "branch_name",
            key: "branch_name",
            sorter: () => {},
        },
        {
            title: "Committer",
            dataIndex: "committer_name",
            key: "committer_email",
            sorter: () => {},
        },
        {
            title: "Commit ID",
            dataIndex: "commit_id",
            key: "commit_id",
            sorter: () => {},
        },
        {
            title: "Committed At",
            dataIndex: "commit_created_at",
            key: "commit_created_at",
            sorter: () => {},
        },
        {
            title: "Lines Changed",
            render: (text, record) => (
                <>
                    <span style={{ color: "green", fontWeight: "bold" }}>
                        {`+ ${record.added_lines} / `}
                    </span>
                    <span style={{ color: "red", fontWeight: "bold" }}>
                        {`- ${record.deleted_lines}`}
                    </span>
                </>
            ),
        },
    ]);

    const {
        status: gitCommitStatus,
        data: gitCommitRes,
        refetch: getGitCommits,
    } = useQuery(
        "gitCommitTable",
        async () => {
            return await HTTP("/analytics/getGitCommitsMonthlyTableData", {
                ...formData,
            });
        },
        { enabled: false },
    );

    const {
        status: commiterStatus,
        data: commiterRes,
        refetch: getCommiters,
    } = useQuery(
        "gitCommiters",
        async () => {
            return await HTTP("/analytics/getGitCommitterEmails", { staff_id });
        },
        { enabled: false },
    );

    const {
        status: gitProjectStatus,
        data: gitProjectRes,
        refetch: getGitProjects,
    } = useQuery(
        "gitProjects",
        async () => {
            return await HTTP("/analytics/getGitProjects", { staff_id });
        },
        { enabled: false },
    );

    const {
        status: authorStatus,
        data: authorRes,
        refetch: getAuthors,
    } = useQuery(
        "gitAuthors",
        async () => {
            return await HTTP("/analytics/getGitAuthorEmails", { staff_id });
        },
        { enabled: false },
    );

    useEffect(() => {
        getAuthors();
        getCommiters();
        getGitProjects();
        getGitCommits();
    }, []);

    useEffect(() => {
        getGitCommits();
    }, [formData]);

    useEffect(() => {
        if (commiterStatus === "success") {
            const { data: commiters } = commiterRes;
            const newColumn = columns.map((column) => {
                if (column.dataIndex !== "committer_name") {
                    return column;
                }
                column.filters = [];
                column.filterIcon = (filtered) => (
                    <SearchOutlined
                        style={{
                            color: filtered ? "#1890ff" : undefined,
                        }}
                    />
                );
                column.filterSearch = true;
                column.width = "10%";
                commiters.map((commiter) => {
                    column.filters.push({
                        text: commiter.committer_name,
                        value: commiter.committer_email,
                    });
                });
                return column;
            });
            setColumns(newColumn);
        }
    }, [commiterStatus, commiterRes]);

    useEffect(() => {
        if (authorStatus === "success") {
            const { data: authors } = authorRes;
            const newColumn = columns.map((column) => {
                if (column.dataIndex !== "author_name") {
                    return column;
                }
                column.filters = [];
                column.filterIcon = (filtered) => (
                    <SearchOutlined
                        style={{
                            color: filtered ? "#1890ff" : undefined,
                        }}
                    />
                );
                column.filterSearch = true;
                column.width = "10%";
                authors.map((author) => {
                    column.filters.push({
                        text: author.author_name,
                        value: author.author_email,
                    });
                });
                return column;
            });
            setColumns(newColumn);
        }
    }, [authorStatus, authorRes]);

    useEffect(() => {
        if (gitProjectStatus === "success") {
            const { data: projects } = gitProjectRes;
            const newColumn = columns.map((column) => {
                if (column.dataIndex !== "project_name") {
                    return column;
                }
                column.filters = [];
                column.filterSearch = true;
                column.filterIcon = (filtered) => (
                    <SearchOutlined
                        style={{
                            color: filtered ? "#1890ff" : undefined,
                        }}
                    />
                );
                column.width = "10%";
                projects.map((project) => {
                    column.filters.push({
                        text: project.project_name,
                        value: project.project_id,
                    });
                });
                return column;
            });
            setColumns(newColumn);
        }
    }, [gitProjectStatus, gitProjectRes]);

    useEffect(() => {
        if (gitCommitStatus === "success") {
            const { data: tableData, total } = gitCommitRes;
            setTableData(tableData);
            setTotalRecords(total);
        }
    }, [gitCommitStatus, gitCommitRes]);

    function handleTableChange(pagination, filters, sorter) {
        const { current, pageSize } = pagination;
        const { order, field } = sorter;
        const others = {
            page: current - 1,
            limit: pageSize,
        };
        if (order !== undefined) {
            others.sorting = order === "ascend" ? 1 : 2;
            others.sort_by = field;
        } else {
            delete formData.sorting;
            delete formData.sort_by;
        }

        const newFormData = {
            ...formData,
            ...others,
            filters,
        };
        setFormData(newFormData);
    }

    return (
        <Card className="dashboard-container">
            <CustomHeader />
            {gitCommitStatus === "loading" || gitCommitStatus === "idle" ? (
                <Skeleton
                    active
                    title={false}
                    paragraph={{ rows: 20, width: 40 }}
                />
            ) : (
                tableData.length > 0 && (
                    <CustomTable
                        columns={columns}
                        tableData={tableData}
                        noOfrecords={totalRecords}
                        handleTableChange={handleTableChange}
                        rowKey="id"
                        className="dashboard-table"
                        isScroll
                    />
                )
            )}
        </Card>
    );
}

export { GitDashboardTable };
